import { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Button, Form, FormControl, FormLabel, Spinner, Alert, InputGroup
} from 'react-bootstrap';

import StatusAlert from '../components/StatusAlert';
import useAuth from '../hooks/useAuth';
import useForm from '../hooks/useForm';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import './auth.css';

function redirectPath(search) {
  const match = search.match(/redirect=(.*)/);
  const redirect = match?.[1];
  return redirect ? decodeURIComponent(redirect) : '/console';
}

function Login() {
  const title = 'Login';

  const [isLoading, setIsLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [userId, setUserId] = useState(null);
  const [message, setMessage] = useState(null);
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const alertOpts = useRef({ isShow: false, message: '' });

  const handleDismiss = () => {
    alertOpts.current.isShow = false;
  };

  const handleLogin = async (e, data) => {
    try {
      setIsLoading(true);
      setMessage(null);
      const response = await login(data.username, data.password);

      if (response.require_2fa) {
        setRequire2FA(true);
        setUserId(response.user_id);

      } else {
        navigate(redirectPath(search));
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e, data) => {
    try {
      setIsLoading(true);
      setMessage(null);
      await verify2FA(userId, data.twoFACode.trim());
      navigate(redirectPath(search));
    } catch (err) {
      setMessage({ type: 'danger', text: "Invalid 2FA code" });
    } finally {
      setIsLoading(false);
    }
  };

  const loginValidators = {
    username: {
      required: {
        value: true,
        message: 'username is required',
      },
    },
    password: {
      required: {
        value: true,
        message: 'password is required',
      },
    },
    agree: {
      required: {
        value: true,
        message: 'You must agree before submitting',
      },
    },
  };

  const twoFAValidators = {
    twoFACode: {
      required: {
        value: true,
        message: '2FA code is required',
      },
    },
  };

  const {
    data: loginData,
    handleChange: handleLoginChange,
    handleSubmit: handleLoginSubmit,
    errors: loginErrors,
  } = useForm({
    onSubmit: handleLogin,
    validators: loginValidators,
  });

  const {
    data: twoFAData,
    handleChange: handle2FAChange,
    handleSubmit: handle2FASubmit,
    errors: twoFAErrors,
  } = useForm({
    onSubmit: handleVerify2FA,
    validators: twoFAValidators,
  });

  useEffect(() => {
    console.log("require2FA state:", require2FA);
  }, [require2FA]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const handleCheckboxChange = (e) => {
    handleLoginChange('agree')({ target: { value: e.target.checked } });
  };
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className="container-fluid mb-5 px-3">
        <div className="row mx-auto align-content-center" style={{ maxWidth: "30rem", minHeight: "70vh" }}>
          <div className="card card-lg bg-white p-4" >
            <div className="card-body">
              <Form noValidate onSubmit={require2FA ? handle2FASubmit : handleLoginSubmit}>

                {!require2FA ? (
                  <>
                    <div className="mb-4">
                      <h4 className="text-secondary mb-1">Account Login</h4>
                      <p className="small my-0">Enter your credentials to log in.</p>
                    </div>
                    <Form.Group className="form-floating mb-3" controlId="inputUsername">
                      <FormControl type="text"
                        className="form-control form-input-top"
                        isInvalid={loginErrors?.username}
                        placeholder="Username"
                        onChange={handleLoginChange('username')}
                        value={loginData.username || ''}
                      />
                      <FormLabel>Username</FormLabel>
                      {loginErrors?.username && <Form.Control.Feedback type="invalid">{loginErrors.username}</Form.Control.Feedback>}
                    </Form.Group>
                    <Form.Group className="form-floating mb-3" controlId="inputPassword">
                      <InputGroup>
                        <FormControl
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          className="form-control rounded-end"
                          onChange={handleLoginChange('password')}
                          value={loginData.password || ''}
                          isInvalid={!!loginErrors?.password}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={togglePasswordVisibility}
                          className="password-toggle rounded-end"
                        >
                          {showPassword ? <EyeSlash /> : <Eye />}
                        </Button>
                        <FormLabel>Password</FormLabel>
                      </InputGroup>
                      {loginErrors?.password && <Form.Control.Feedback type="invalid">{loginErrors.password}</Form.Control.Feedback>}

                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="agree-checkbox"
                        className="form-check-label ms-0"
                        onChange={handleCheckboxChange}
                        isInvalid={!!loginErrors?.agree}
                        checked={loginData.agree || false}
                        label={
                          <span className="small">
                            By clicking, I agree to the <a href='../../legal/privacyPolicy.html' target='_blank'>Privacy Policy</a> and <a href='../../legal/termsOfService.html' target='_blank'>Terms of Service</a>
                          </span>
                        }
                      />
                      {loginErrors?.agree && <Form.Control.Feedback type="invalid">{loginErrors.agree}</Form.Control.Feedback>}
                    </Form.Group>

                  </>
                ) : (
                  <>
                   <div className="mb-4">
                      <h4 className="text-secondary mb-1">Verify Your Identity</h4>
                      <p className="small my-0">Please check your email for the two-factor authentication (2FA Code) and enter below</p>
                    </div>
                    
                    <Form.Group className="form-floating" controlId="inputTwoFACode">
                      <FormControl type="text"
                        className="form-control"
                        isInvalid={twoFAErrors?.twoFACode}
                        placeholder="2FA Code"
                        onChange={handle2FAChange('twoFACode')}
                        value={twoFAData.twoFACode || ''}
                      />
                      <FormLabel>2FA Code</FormLabel>
                      {twoFAErrors?.twoFACode && <Form.Control.Feedback type="invalid">{twoFAErrors.twoFACode}</Form.Control.Feedback>}
                    </Form.Group>
                  </>
                )}

                <Button className="btn btn-lg btn-primary mt-3"
                  type="submit"
                  disabled={isLoading}
                  onClick={require2FA ? handle2FASubmit : handleLoginSubmit}
                >
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isLoading} />
                  <span className="px-2">{require2FA ? 'Verify' : 'Login'}</span>
                </Button>

                {message && (
                  <Alert variant={message.type} className="mt-3">
                    {message.text}
                  </Alert>
                )}
              </Form>
            </div>
          </div>

          {!require2FA && (
            <>
              <p class="mt-2 mb-0 note text-muted small">Don't have an account yet? <Link to="/signup" >Sign Up here</Link></p>
              <p className="text-muted small" ><Link to="/forgot" >Forgot Password?</Link> </p>
            </>
          )}
        </div>
      </div>

      <StatusAlert show={alertOpts.current.isShow}
        variant="failure"
        message={alertOpts.current.message}
        onDismiss={handleDismiss}
      />
    </>
  );
}

export default Login;